<template>
  <EmsLayout>
    <div class="pcs-monitor-page" v-loading="loading">
      <!-- 顶部栏 -->
      <div class="pcs-header">
        <div class="pcs-title">PCS实时信息</div>

        <!-- 数据采集时间 + 日期切换 -->
        <div class="data-time">
          <span class="time-label">数据采集时间</span>
          <span class="time-value">{{ dataTime || '--' }}</span>
          <span class="time-nav">
            <DateNavigator v-model="chartDate" @change="onDateChange" />
          </span>
        </div>
      </div>

      <!-- 数据表格 -->
      <div class="data-table">
        <!-- 并离网状态 + 控制状态 -->
        <div class="table-row status-table-row">
          <div class="table-cell" style="grid-column: 1 / 3">
            <span class="cell-label">并离网状态</span>
            <div class="status-tags">
              <span :class="['status-tag', gridStatus.invalid ? 'active' : '']">
                <i
                  :class="gridStatus.invalid ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                无效
              </span>
              <span :class="['status-tag', gridStatus.onGrid ? 'active' : '']">
                <i
                  :class="gridStatus.onGrid ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                并网
              </span>
              <span :class="['status-tag', gridStatus.offGrid ? 'active' : '']">
                <i
                  :class="gridStatus.offGrid ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                离网
              </span>
            </div>
          </div>
          <div class="table-cell" style="grid-column: 3 / 5">
            <span class="cell-label">控制状态</span>
            <div class="status-tags">
              <span :class="['status-tag', ctrlStatus.localManual ? 'active' : '']">
                <i
                  :class="ctrlStatus.localManual ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                本地手动控制状态
              </span>
              <span :class="['status-tag', ctrlStatus.localAuto ? 'active' : '']">
                <i
                  :class="ctrlStatus.localAuto ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                本地自动控制状态
              </span>
              <span :class="['status-tag', ctrlStatus.remote ? 'active' : '']">
                <i
                  :class="ctrlStatus.remote ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                远程控制状态
              </span>
            </div>
          </div>
        </div>

        <!-- 故障状态 + 运行状态 -->
        <div class="table-row status-table-row">
          <div class="table-cell" style="grid-column: 1 / 3">
            <span class="cell-label">故障状态</span>
            <div class="status-tags">
              <span :class="['status-tag', faultStatus.normal ? 'active' : '']">
                <i
                  :class="faultStatus.normal ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                无故障
              </span>
              <span :class="['status-tag', faultStatus.alarm ? 'active' : '']">
                <i
                  :class="faultStatus.alarm ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                告警
              </span>
              <span :class="['status-tag', faultStatus.fault ? 'active' : '']">
                <i
                  :class="faultStatus.fault ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                故障
              </span>
            </div>
          </div>
          <div class="table-cell" style="grid-column: 3 / 5">
            <span class="cell-label">运行状态</span>
            <div class="status-tags">
              <span :class="['status-tag', runStatus.stop ? 'active' : '']">
                <i
                  :class="runStatus.stop ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                停止
              </span>
              <span :class="['status-tag', runStatus.start ? 'active' : '']">
                <i
                  :class="runStatus.start ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                启动
              </span>
              <span :class="['status-tag', runStatus.starting ? 'active' : '']">
                <i
                  :class="runStatus.starting ? 'el-icon-success' : 'el-icon-circle-check'"
                  class="status-icon"></i>
                启动中
              </span>
            </div>
          </div>
        </div>

        <div class="table-row">
          <div class="table-cell">
            <span class="cell-label">ab交流线电压</span>
            <span class="cell-value">
              {{ metrics.v_ac_a }}
              <span class="unit">(V)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">bc交流线电压</span>
            <span class="cell-value">
              {{ metrics.v_ac_b }}
              <span class="unit">(V)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">ca交流线电压</span>
            <span class="cell-value">
              {{ metrics.v_ac_c }}
              <span class="unit">(V)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">交流A相电流</span>
            <span class="cell-value">
              {{ metrics.c_ac_a }}
              <span class="unit">(A)</span>
            </span>
          </div>
        </div>
        <div class="table-row">
          <div class="table-cell">
            <span class="cell-label">交流B相电流</span>
            <span class="cell-value">
              {{ metrics.c_ac_b }}
              <span class="unit">(A)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">交流C相电流</span>
            <span class="cell-value">
              {{ metrics.c_ac_c }}
              <span class="unit">(A)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">交流有功功率</span>
            <span class="cell-value">
              {{ metrics.w1 }}
              <span class="unit">(kW)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">交流无功功率</span>
            <span class="cell-value">
              {{ metrics.w2 }}
              <span class="unit">(kVar)</span>
            </span>
          </div>
        </div>
        <div class="table-row">
          <div class="table-cell">
            <span class="cell-label">交流视在功率</span>
            <span class="cell-value">
              {{ metrics.w3 }}
              <span class="unit">(kVA)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">功率因数</span>
            <span class="cell-value">{{ metrics.wf }}</span>
          </div>
          <div class="table-cell">
            <span class="cell-label">交流频率</span>
            <span class="cell-value">
              {{ metrics.hz }}
              <span class="unit">(Hz)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">总直流功率</span>
            <span class="cell-value">
              {{ metrics.w_dc }}
              <span class="unit">(kW)</span>
            </span>
          </div>
        </div>
        <div class="table-row">
          <div class="table-cell">
            <span class="cell-label">总直流电压</span>
            <span class="cell-value">
              {{ metrics.v_dc }}
              <span class="unit">(V)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">总直流电流</span>
            <span class="cell-value">
              {{ metrics.c_dc }}
              <span class="unit">(A)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">环境温度</span>
            <span class="cell-value">
              {{ metrics.t_env }}
              <span class="unit">(℃)</span>
            </span>
          </div>
          <div class="table-cell">
            <span class="cell-label">模块温度</span>
            <span class="cell-value">
              {{ metrics.t_mode }}
              <span class="unit">(℃)</span>
            </span>
          </div>
        </div>
        <div class="table-row single">
          <div class="table-cell">
            <span class="cell-label">有功功率设置值</span>
            <span class="cell-value">
              {{ metrics.w_set }}
              <span class="unit">(kW)</span>
            </span>
          </div>
        </div>
      </div>

      <!-- 图表区域 -->
      <div class="charts-grid">
        <div class="chart-panel">
          <div class="chart-header">
            <span class="chart-dot"></span>
            <span class="chart-title">温度实时曲线</span>
          </div>
          <div class="chart-legend">
            <span class="legend-item">
              <i class="legend-icon" style="background: #4d8cfd"></i>
              环境温度(℃)
            </span>
            <span class="legend-item">
              <i class="legend-icon" style="background: #02c498"></i>
              模块温度(℃)
            </span>
          </div>
          <div ref="tempChart" class="chart-container"></div>
        </div>

        <div class="chart-panel">
          <div class="chart-header">
            <span class="chart-dot"></span>
            <span class="chart-title">日功率曲线</span>
          </div>
          <div class="chart-legend">
            <span class="legend-item">
              <i class="legend-icon" style="background: #4d8cfd"></i>
              有功功率(kW)
            </span>
            <span class="legend-item">
              <i class="legend-icon" style="background: #02c498"></i>
              无功功率(kVar)
            </span>
            <span class="legend-item">
              <i class="legend-icon" style="background: #f5a623"></i>
              视在功率(kVA)
            </span>
          </div>
          <div ref="powerChart" class="chart-container"></div>
        </div>
      </div>
    </div>
  </EmsLayout>
</template>

<script>
  import * as echarts from 'echarts';
  import dayjs from 'dayjs';
  import EmsLayout from '../layout/EmsLayout.vue';
  import DateNavigator from '../components/DateNavigator.vue';
  import { getPcsRealtimeData, queryEmsHistory } from '@/service/emsApis';
  import { formatNum, default24hLabels, doubleLineOption, tripleLineOption } from '../utils';

  export default {
    name: 'PcsMonitor',
    components: {
      EmsLayout,
      DateNavigator,
    },
    data() {
      return {
        chartDate: dayjs().format('YYYY-MM-DD'),
        dataTime: '',
        // 并离网状态
        gridStatus: { onGrid: false, invalid: false, offGrid: true },
        // 控制状态
        ctrlStatus: { localManual: false, localAuto: false, remote: true },
        // 运行状态
        runStatus: { stop: true, start: false, starting: false },
        // 故障状态
        faultStatus: { normal: true, alarm: false, fault: false },
        // 指标数据
        metrics: {
          v_ac_a: '--',
          v_ac_b: '--',
          v_ac_c: '--',
          c_ac_a: '--',
          c_ac_b: '--',
          c_ac_c: '--',
          w1: '--',
          w2: '--',
          w3: '--',
          wf: '--',
          hz: '--',
          w_dc: '--',
          v_dc: '--',
          c_dc: '--',
          t_env: '--',
          t_mode: '--',
          w_set: '--',
        },
        tempChartInstance: null,
        powerChartInstance: null,
        refreshTimer: null,
        loading: false,
      };
    },
    computed: {
      stationCode() {
        return this.$store.getters.emsStationCode;
      },
    },
    watch: {
      stationCode() {
        this.fetchAllData();
      },
    },
    mounted() {
      this.$nextTick(() => {
        this.fetchAllData();
      });
      window.addEventListener('resize', this.handleResize);
      this.refreshTimer = setInterval(() => {
        this.fetchRealtimeData();
      }, 30000);
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.handleResize);
      this.disposeCharts();
      if (this.refreshTimer) clearInterval(this.refreshTimer);
    },
    methods: {
      fetchAllData() {
        this.loading = true;
        Promise.all([this.fetchRealtimeData(), this.fetchChartData()]).finally(() => {
          this.loading = false;
        });
      },

      async fetchRealtimeData() {
        try {
          const res = await getPcsRealtimeData(this.stationCode, 0);
          if (!res) return;
          this.parsePcsData(res);
        } catch (e) {
          console.error('获取PCS实时数据失败', e);
        }
      },

      parsePcsData(data) {
        // 数据采集时间
        if (data.attm) {
          this.dataTime = dayjs(data.attm).format('YYYY-MM-DD HH:mm:ss');
        }

        // 并离网状态: st_oo 0=无效 1=并网 2=离网
        const stOo = data.st_oo != null ? data.st_oo : 0;
        this.gridStatus = {
          onGrid: stOo === 1,
          offGrid: stOo === 2,
          invalid: stOo === 0,
        };

        // 控制状态: st_ctrl 1=本地手动 2=本地自动 4=远程
        const stCtrl = data.st_ctrl != null ? data.st_ctrl : 4;
        this.ctrlStatus = {
          localManual: stCtrl === 1,
          localAuto: stCtrl === 2,
          remote: stCtrl === 4,
        };

        // 运行状态: st_run 0=停止 1=启动 2=启动中
        const stRun = data.st_run != null ? data.st_run : 0;
        this.runStatus = {
          stop: stRun === 0,
          start: stRun === 1,
          starting: stRun === 2,
        };

        // 故障状态: st_err 使用位运算（与BMS st_sys风格一致）
        // bit0(1)=故障, bit1(2)=告警; 示例数据 st_err=4 表示bit2保留位，非典型但需容错
        const stErr = data.st_err != null ? data.st_err : 0;
        this.faultStatus = {
          normal: stErr === 0,
          fault: (stErr & 1) !== 0, // eslint-disable-line no-bitwise
          alarm: (stErr & 2) !== 0, // eslint-disable-line no-bitwise
        };

        // 指标数据
        this.metrics.v_ac_a = this.formatNum(data.v_ac_a);
        this.metrics.v_ac_b = this.formatNum(data.v_ac_b);
        this.metrics.v_ac_c = this.formatNum(data.v_ac_c);
        this.metrics.c_ac_a = this.formatNum(data.c_ac_a);
        this.metrics.c_ac_b = this.formatNum(data.c_ac_b);
        this.metrics.c_ac_c = this.formatNum(data.c_ac_c);
        this.metrics.w1 = this.formatNum(data.w1);
        this.metrics.w2 = this.formatNum(data.w2);
        this.metrics.w3 = this.formatNum(data.w3);
        this.metrics.wf = this.formatNum(data.wf);
        this.metrics.hz = this.formatNum(data.hz);
        this.metrics.w_dc = this.formatNum(data.w_dc);
        this.metrics.v_dc = this.formatNum(data.v_dc);
        this.metrics.c_dc = this.formatNum(data.c_dc);
        this.metrics.t_env = this.formatNum(data.t_env);
        this.metrics.t_mode = this.formatNum(data.t_mode);
        this.metrics.w_set = this.formatNum(data.w_set);
      },

      async fetchChartData() {
        try {
          const date = this.chartDate;
          const res = await queryEmsHistory({
            proc: 'pcs_m_chart',
            param: [this.stationCode, `${date} 00:00:00`, `${date} 23:59:59`],
          });
          const list = Array.isArray(res) ? res : res?.data || [];
          this.renderAllCharts(list);
        } catch (e) {
          console.error('获取PCS曲线数据失败', e);
        }
      },

      renderAllCharts(list) {
        const xData = [];
        const tEnvData = [];
        const tModeData = [];
        const w1Data = [];
        const w2Data = [];
        const w3Data = [];

        if (list.length > 0) {
          list.forEach(item => {
            xData.push(item.attm ? dayjs(item.attm).format('HH:mm') : '');
            tEnvData.push(item.t_env ?? null);
            tModeData.push(item.t_mode ?? null);
            // pcs_m_chart 历史曲线字段为 acw1/acw2/acw3（实时接口为 w1/w2/w3，命名不一致）
            w1Data.push(item.acw1 ?? null);
            w2Data.push(item.acw2 ?? null);
            w3Data.push(item.acw3 ?? null);
          });
        } else {
          xData.push(...default24hLabels());
        }

        this.renderTempChart(xData, tEnvData, tModeData);
        this.renderPowerChart(xData, w1Data, w2Data, w3Data);
      },

      renderTempChart(xData, y1Data, y2Data) {
        const chartDom = this.$refs.tempChart;
        if (!chartDom) return;
        if (!this.tempChartInstance) {
          this.tempChartInstance = echarts.init(chartDom);
        }
        const option = doubleLineOption(
          '环境温度(℃)',
          '#4d8cfd',
          '模块温度(℃)',
          '#02c498',
          xData,
          y1Data,
          y2Data
        );
        this.tempChartInstance.setOption(option, true);
      },

      renderPowerChart(xData, y1Data, y2Data, y3Data) {
        const chartDom = this.$refs.powerChart;
        if (!chartDom) return;
        if (!this.powerChartInstance) {
          this.powerChartInstance = echarts.init(chartDom);
        }
        const option = tripleLineOption(
          ['有功功率(kW)', '无功功率(kVar)', '视在功率(kVA)'],
          ['#4d8cfd', '#02c498', '#f5a623'],
          xData,
          [y1Data, y2Data, y3Data]
        );
        this.powerChartInstance.setOption(option, true);
      },

      formatNum,

      onDateChange() {
        this.fetchChartData();
      },

      handleResize() {
        if (this.tempChartInstance) this.tempChartInstance.resize();
        if (this.powerChartInstance) this.powerChartInstance.resize();
      },
      disposeCharts() {
        if (this.tempChartInstance) this.tempChartInstance.dispose();
        if (this.powerChartInstance) this.powerChartInstance.dispose();
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '../styles/variables.scss';

  .pcs-monitor-page {
    width: 100%;
    height: 100%;
    background: $bg-primary;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  // 顶部栏
  .pcs-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
  }

  .pcs-title {
    font-size: 14px;
    font-weight: 600;
    color: $accent-blue;
  }

  .data-time {
    display: flex;
    align-items: center;
    gap: 8px;

    .time-label {
      font-size: 13px;
      color: $text-secondary;
    }

    .time-value {
      font-size: 13px;
      color: $accent-blue;
      font-weight: 500;
    }

    .time-nav {
      margin-left: 4px;
    }
  }

  // 数据表格
  .data-table {
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
    overflow: hidden;
  }

  .status-table-row {
    grid-template-columns: repeat(4, 1fr);

    .table-cell {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;

      .status-tags {
        display: flex;
        align-items: center;
        gap: 8px;

        .status-tag {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
          color: $text-secondary;
          background: #f6f7f8;
          border: 1px solid transparent;
          white-space: nowrap;

          .status-icon {
            font-size: 11px;
          }

          &.active {
            color: $accent-green;
            border-color: rgba(0, 255, 136, 0.3);
            background: rgba(0, 255, 136, 0.05);
          }
        }
      }
    }
  }

  .table-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border-bottom: 1px solid #ebebf0;

    &:last-child {
      border-bottom: none;
    }

    &.single {
      grid-template-columns: 1fr 3fr;
    }
  }

  .table-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    border-right: 1px solid #ebebf0;

    &:last-child {
      border-right: none;
    }

    .cell-label {
      font-size: 12px;
      color: $text-secondary;
    }

    .cell-value {
      font-size: 14px;
      font-weight: 500;
      color: $text-primary;
      font-family: 'DIN Alternate', 'Helvetica Neue', Arial, sans-serif;

      .unit {
        font-size: 11px;
        color: $text-secondary;
        font-weight: normal;
        margin-left: 2px;
      }
    }
  }

  // 图表区域
  .charts-grid {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    min-height: 0;
  }

  .chart-panel {
    background: $bg-card;
    border: 1px solid $bg-card-border;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .chart-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;

    .chart-dot {
      width: 4px;
      height: 14px;
      background: $accent-blue;
      border-radius: 2px;
    }

    .chart-title {
      font-size: 14px;
      font-weight: 600;
      color: $text-primary;
    }
  }

  .chart-legend {
    display: flex;
    gap: 16px;
    margin-bottom: 6px;
    flex-wrap: wrap;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 11px;
      color: $text-secondary;
    }

    .legend-icon {
      width: 12px;
      height: 3px;
      border-radius: 2px;
    }
  }

  .chart-container {
    flex: 1;
    min-height: 180px;
  }
</style>
